<template>
  <BaseModal
    :value="true"
    @input="$emit('close')"
    title="Address"
    id="infoauto-create-address"
  >
    <div class="flex flex-col p-4">
      <h1 class="txt-lg font-bold mb-4">
        Add Address to customer "{{ customerName }}"
      </h1>
      <form @submit.prevent="submit" class="address-form" v-if="!loading">
        <div class="address-form-grid">
          <div class="form-field">
            <label class="form-label">Name *</label>
            <input
              :value="name"
              class="regular full bordered bg-gray-100 cursor-not-allowed"
              readonly
            />
            <p class="text-xs text-gray-500 mt-1">
              {{ $t("message.address_form.name_locked_hint") }}
            </p>
          </div>

          <div class="form-field">
            <label class="form-label">Company</label>
            <input
              v-model="company"
              placeholder="Enter company"
              class="regular full bordered"
            />
          </div>

          <GoogleAddressSearch
            v-model="street1"
            label="Street 1 *"
            placeholder="Start typing your address..."
            @address-selected="handleGoogleAddressSelected"
          />

          <div class="form-field">
            <label class="form-label">Street 2</label>
            <input
              v-model="street2"
              class="regular full bordered"
              placeholder="Enter address 2"
            />
          </div>

          <div class="form-field">
            <label class="form-label">Country *</label>
            <select
              class="regular full bordered"
              type="text"
              name="country"
              v-model="country"
              required
            >
              <option value="" disabled selected>Select a country</option>
              <option
                v-for="country in countriesWithSelected"
                :value="country.code"
                :key="country.code"
                >{{ country.label }}</option
              >
            </select>
          </div>

          <InfoautoStateCityFields
            :country="country"
            :state.sync="state"
            :city.sync="city"
          />

          <div class="form-field">
            <label class="form-label">Postal Code *</label>
            <input
              v-model="zip"
              placeholder="Enter postal code"
              class="regular full bordered"
              required
            />
          </div>
        </div>

        <div
          v-if="error"
          class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-6"
        >
          {{ error }}
        </div>

        <button type="submit" class="btn-dark-button w-40 mt-6">Create</button>
      </form>
      <div v-else>
        <BaseSmallSpinner />
      </div>
    </div>
  </BaseModal>
</template>

<script>
import {
  addAddressToCustomer,
  getInfoautoCountries
} from "@/api/store/Infoauto.js";
import GoogleAddressSearch from "@/components/global/GoogleAddressSearch.vue";
import InfoautoStateCityFields from "@/components/Infoauto/InfoautoStateCityFields.vue";

export default {
  components: {
    GoogleAddressSearch,
    InfoautoStateCityFields
  },
  props: {
    customerName: {
      type: String,
      required: true
    },
    customerInfoautoId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      // Infoauto no guarda nombre por direccion: siempre es el del cliente
      name: this.customerName,
      company: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      countries: [],
      loading: false,
      error: ""
    };
  },
  computed: {
    countriesWithSelected() {
      const selectedCountry = this.country;
      const countryExists = this.countries.some(
        country => country.code === selectedCountry
      );
      if (!countryExists && selectedCountry) {
        return [
          ...this.countries,
          { code: selectedCountry, label: selectedCountry }
        ];
      }
      return this.countries;
    }
  },
  created() {
    this.loadCountries();
  },
  methods: {
    async loadCountries() {
      try {
        this.countries = await getInfoautoCountries();
      } catch (error) {
        console.error("Error loading countries:", error);
        this.$toast.error("Failed to load countries");
      }
    },
    submit() {
      if (!this.addressIsValid()) {
        return;
      }
      this.error = "";
      this.loading = true;
      console.log(
        "creating address for customer",
        this.name,
        this.company,
        this.street1,
        this.street2,
        this.city,
        this.state,
        this.zip,
        this.country
      );
      const newAddress = {
        name: this.name,
        company: this.company,
        street1: this.street1,
        street2: this.street2,
        city: this.city,
        state: this.state,
        postalCode: this.zip,
        country: this.country
      };
      addAddressToCustomer(this.customerInfoautoId, newAddress)
        .then(data => {
          console.log("Address created", data.address);
          this.loading = false;
          const addressCreated = {
            // dir_id de Infoauto: para poder guardarla como predeterminada
            infoautoAddressId: data.address.dirId || "",
            name: this.customerName,
            company: "",
            street1: data.address.calleCasa,
            street2: "",
            street3: "",
            city: data.address.ciudad,
            state: data.address.codEstado,
            postalCode: data.address.codPostal,
            country: data.address.codPais
          };
          this.$emit("addressCreated", addressCreated);
        })
        .catch(error => {
          console.error("Error creating address:", error);
          this.error =
            error.response?.data?.message || "Failed to create address";
          this.$toast.error(this.error);
          this.loading = false;
        });
    },
    handleGoogleAddressSelected(addressData) {
      console.log("=== Address selected from GoogleAddressSearch ===");
      console.log("Parsed address data:", JSON.stringify(addressData, null, 2));

      // Fill all the address fields from Google data
      if (addressData.street1) {
        this.street1 = addressData.street1;
      }
      if (addressData.city) {
        this.city = addressData.city;
      }
      if (addressData.state) {
        this.state = addressData.state;
      }
      if (addressData.postal_code) {
        this.zip = addressData.postal_code;
      }
      if (addressData.country) {
        this.country = addressData.country;
      }

      console.log("Updated form fields:", {
        street1: this.street1,
        city: this.city,
        state: this.state,
        zip: this.zip,
        country: this.country
      });
    },
    addressIsValid() {
      if (this.name === "") {
        this.$toast.error("Name is required");
        return false;
      }
      if (this.street1 === "") {
        this.$toast.error("Address is required");
        return false;
      }
      if (this.city === "") {
        this.$toast.error("City is required");
        return false;
      }
      if (this.state === "") {
        this.$toast.error("State is required");
        return false;
      }
      if (this.zip === "") {
        this.$toast.error("Zip is required");
        return false;
      }
      if (this.country === "") {
        this.$toast.error("Country is required");
        return false;
      }
      return true;
    }
  }
};
</script>
<style lang="scss">
#infoauto-create-address {
  .modal-content {
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    height: auto;
    overflow-y: auto;
  }
}

.address-form {
  display: flex;
  flex-direction: column;
}

.address-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}
</style>
